// @flow

/**
 * The original author of the CSS chart is Nick Springer. Thanks him for the contribution.
 * A few simple modifications were made here to follow the SDK coding styles.
 * This class is most suitable for PlaceClickControl.
 */

import {extend} from './util';
import DOM from './dom';

type ChartOptions = {
    barHeight: number; // has to be a positive number
    chartTitle: ?string;
    dataColor: ?boolean;
    dataColorBreaks: Array<number>;
    dataColorValues: Array<string>;
    footNote: ?string;
};

const defaultOptions: ChartOptions = {
    barHeight: 100,
    chartTitle: 'Average Dwell Time by Hour (in minutes)',
    dataColor: false, // because dwell times range can be small or large (9,000). It is hard to find a proper default data color breaks.
    dataColorBreaks: [20, 40],
    dataColorValues: ['#368fc7', '#73b847', '#fbad26', '#e86363'],
    footNote: '',
};

class DwellTimesChart {
    _options: ChartOptions;

    constructor(options: ?ChartOptions) {
        this._options = extend({}, defaultOptions, options);
    }

    _getDwellTimeColor(dwellTime: number): string {
        if (this._options.dataColorBreaks.length < 2) {
            throw new Error(`'dataColorBreaks' length has to be 2 or more. Values have to be incremental.
                Sample value: [20, 40]`);
        }
        if (this._options.dataColorValues.length < 4) {
            throw new Error(`'dataColorValues' length has to be 4 or more. When dataColor is false, the color code at index 0 will be used. 
                When dataColor is true, color codes after index 0 will be used. Sample value: ['#368fc7', '#73b847', '#fbad26', '#e86363']`);
        }
        if (this._options.dataColorValues.length - this._options.dataColorBreaks.length !== 2) {
            throw new Error(`'dataColorValues' array must have 2 more items than 'dataColorBreaks'`);
        }
        let color = this._options.dataColorValues[0];
        if (this._options.dataColor === true) {
            this._options.dataColorBreaks.forEach((value, index) => {
                if (index === 0) {
                    if (dwellTime < value) {
                        color = this._options.dataColorValues[index + 1];
                    }
                } else {
                    if (
                        dwellTime >= this._options.dataColorBreaks[index - 1] &&
                        dwellTime < value
                    ) {
                        color = this._options.dataColorValues[index + 1];
                    } else if (dwellTime >= value) {
                        if (index === this._options.dataColorBreaks.length - 1) {
                            color = this._options.dataColorValues[index + 2];
                        }
                    }
                }
            });
        }
        return color;
    }

    createChart(dataArray: Array<number>, options: ChartOptions): HTMLElement {
        this._options = extend(this._options, options);

        const chartContainer = DOM.create('div');
        chartContainer.style.display = 'flex';
        chartContainer.style.flexDirection = 'column';
        chartContainer.style.margin = '8px 0';
        chartContainer.style.padding = '5px 10px';

        const title = DOM.create('h6');
        title.style.textAlign = 'center';
        title.style.fontSize = '12px';
        title.style.margin = '0 0 8px 0';
        title.textContent = this._options.chartTitle;
        chartContainer.appendChild(title);

        const chart = DOM.create('div', 'dwell-time-chart');
        chart.id = 'chart';
        chart.style.width = '100%';
        chart.style.display = 'flex';
        chart.style.flexDirection = 'row';
        chart.style.justifyContent = 'space-evenly';

        dataArray.forEach((dataPoint, index) => {
            const barContainer = DOM.create('div');
            barContainer.style.display = 'flex';
            barContainer.style.flexDirection = 'column';
            barContainer.style.alignItems = 'center';
            barContainer.style.minWidth = '20px';
            barContainer.style.justifyContent = 'flex-end';

            const dataLabel = DOM.create('div');
            dataLabel.style.marginBottom = '2px';
            dataLabel.style.fontSize = '10px';
            dataLabel.style.transform = 'rotate(-90deg)';
            dataLabel.textContent = dataPoint === 0 ? '' : dataPoint;
            barContainer.appendChild(dataLabel);

            const dataMax = Math.max(...dataArray);
            const bar = DOM.create('div');
            bar.style.width = '12px';
            bar.style.height = `${(dataPoint / dataMax) * this._options.barHeight}px`;
            bar.style.backgroundColor = this._getDwellTimeColor(dataPoint);
            barContainer.appendChild(bar);

            const hour = DOM.create('div');
            hour.style.borderTop = '1px solid #a0a0a0';
            hour.style.width = '100%';
            hour.style.fontSize = '10px';
            hour.style.textAlign = 'center';
            hour.textContent = index > 12 ? `${index - 12}p` : index === 0 ? '12a' : `${index}a`;
            barContainer.appendChild(hour);

            chart.appendChild(barContainer);
        });
        chartContainer.appendChild(chart);

        if (typeof this._options.footNote !== 'undefined' && this._options.footNote !== null && this._options.footNote !== '') {
            const footNote = DOM.create('div');
            footNote.style.margin = '2px 0 0 0';
            footNote.style.fontSize = '12px';
            footNote.textContent = this._options.footNote;
            chartContainer.appendChild(footNote);
        }

        return chartContainer;
    }
}

export default DwellTimesChart;
